import React from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Receipt, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { LedgerFilterState, LedgerKind } from '../lib/invoices'
import { INVOICE_STATUSES, INVOICE_STATUS_KEY, LEDGER_KIND_KEY } from '../lib/labels'
import type { CostContract, CostInvoiceStatus } from '../types'

const ALL = 'all'
const KINDS: LedgerKind[] = ['INVOICE', 'EXPENSE']
const TRIGGER = 'h-8 w-auto min-w-[150px] text-sm'

interface Props {
  count: number
  filter: LedgerFilterState
  onFilterChange: (filter: LedgerFilterState) => void
  contracts: CostContract[]
  canCreateInvoice: boolean
  canCreateExpense: boolean
  onCreateInvoice: () => void
  onCreateExpense: () => void
}

const InvoicesToolbar: React.FC<Props> = ({ count, filter, onFilterChange, contracts, canCreateInvoice, canCreateExpense, onCreateInvoice, onCreateExpense }) => {
  const { t } = useTranslation()
  const set = (patch: Partial<LedgerFilterState>) => onFilterChange({ ...filter, ...patch })
  return (
    <div className="flex flex-wrap items-center gap-3 py-2">
      <div className="flex items-center gap-1">
        <span className="text-lg font-bold">{count}</span>
        <span className="text-sm text-muted-foreground">{t('Common_total')}</span>
      </div>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        <Select value={filter.kind ?? ALL} onValueChange={(v) => set({ kind: v === ALL ? null : (v as LedgerKind) })}>
          <SelectTrigger className={TRIGGER}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('CostControl_filter_all_ledger_kinds')}</SelectItem>
            {KINDS.map((k) => <SelectItem key={k} value={k}>{t(LEDGER_KIND_KEY[k])}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.status ?? ALL} onValueChange={(v) => set({ status: v === ALL ? null : (v as CostInvoiceStatus) })}>
          <SelectTrigger className={TRIGGER}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('CostControl_filter_all_statuses')}</SelectItem>
            {INVOICE_STATUSES.map((s) => <SelectItem key={s} value={s}>{t(INVOICE_STATUS_KEY[s])}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.contractId ?? ALL} onValueChange={(v) => set({ contractId: v === ALL ? null : v })}>
          <SelectTrigger className="h-8 w-auto min-w-[180px] max-w-[260px] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('CostControl_filter_all_contracts')}</SelectItem>
            {contracts.map((c) => <SelectItem key={c.id} value={c.id}>{c.number ? `${c.number} · ${c.name}` : c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={filter.search} onChange={(e) => set({ search: e.target.value })} placeholder={t('CostControl_ledger_search')} className="h-8 w-[220px] pl-8 pr-8 text-sm" />
          {filter.search && (
            <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => set({ search: '' })} aria-label={t('CostControl_contracts_search_clear')}>
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {canCreateExpense && (
          <Button variant="outline" size="sm" className="h-8" onClick={onCreateExpense}>
            <Plus className="h-4 w-4" />
            {t('CostControl_expense_new')}
          </Button>
        )}
        {canCreateInvoice && (
          <Button size="sm" className="h-8" onClick={onCreateInvoice}>
            <Receipt className="h-4 w-4" />
            {t('CostControl_invoice_new')}
          </Button>
        )}
      </div>
    </div>
  )
}
export default InvoicesToolbar
